import React, { useState, useEffect, useRef } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import axios from "axios";
import * as moment from "moment";
import {
  getDatePickerFormat,
  getDateFormat,
  getCalendarLocale,
} from "@utils/datePickerUtils";
import DatePicker from "react-multi-date-picker";
import { Dialog, Button, TextInput, Image } from "hesus-ui-kit";
import ClipLoader from "react-spinners/ClipLoader";
import StarRatingComponent from "react-star-rating-component";
import CKEditorText from "@components/CKEditorText/CKEditorText";
import UserInfo from "@utils/UserInfo";
import MeetingReportView from "../MeetingReportView/MeetingReportView";
import AttendentList from "./components/AttendentList/AttendentList";
import EntityList from "./components/EntityList/EntityList";
import addIcon from "@icons/ic-add-green.svg";
import deleteIcon from "@icons/ic-delete-red.svg";
import {
  HeaderWrapper,
  Title,
  FormBody,
  OptionsWrapper,
  FlexRow,
  SectionContainer,
  GridRow,
  CKGridRow,
  FileInput,
  FileInputLabel,
  FileImageWrapper,
  ButtonWrapper,
  CKInputLabel,
  FileName,
  RequiredMessage,
  DocumentRow,
  DeleteSpan,
  TextInputLabel,
  ParentDatePicker,
  MeetingReportContainer,
  ContainerLabel,
  RatingWrapper,
} from "./AddEditMeetingReport.styled";
import "./ckeditor.css";
import closeIcon from "@icons/ic-close-white.svg";

const AddEditMeetingReport = (props) => {
  const {
    t,
    id,
    isEditMode,
    isViewMode,
    title,
    onClose,
    togglePopUp,
    updateRefeshStatus,
    objectType,
    objectId,
    countryId,
  } = props;
  const [isMeetingCreating, setIsMeetingCreating] = useState(false);
  const [createMeetingAPIError, setCreateMeetingAPIError] = useState("");
  const meetingReportListByIds = useSelector(
    (state) => state.meetingReportsList.byId
  );

  const selectedMeetingReport = meetingReportListByIds[id];
  const statusRef = useRef();

  const [formData, setFormData] = useState({
    title: {
      required: true,
      value: selectedMeetingReport?.title ?? "",
      error: "",
    },
    date: {
      required: true,
      value: selectedMeetingReport?.meetingDate ?? "",
      error: "",
    },
    description: {
      required: false,
      value: selectedMeetingReport?.description ?? "",
      error: "",
    },
    rating: {
      required: true,
      value: selectedMeetingReport?.rating ?? "",
    },
    nextStepDate: {
      required: false,
      value: selectedMeetingReport?.nextStepDate ?? "",
      error: "",
    },
    nextStepDescription: {
      required: false,
      value: selectedMeetingReport?.nextStepDescription ?? "",
      error: "",
    },

    attendees: {
      required: false,
      value: selectedMeetingReport?.attendees ?? [],
      error: "",
    },

    linkedEntities: {
      required: false,
      value: selectedMeetingReport?.linkedEntities ?? [],
      error: "",
    },
    documents: [],
  });

  const [existingDocuments, setExistingDocuments] = useState([]);

  const [descriptionData, setDescriptionData] = useState(
    selectedMeetingReport?.description ?? ""
  );

  const [nextStepDescriptionData, setNextDescriptionData] = useState(
    selectedMeetingReport?.nextStepDescription ?? ""
  );

  useEffect(() => {
    setFormData({
      ...formData,
      description: {
        value: descriptionData,
        error: "",
        required: true,
      },
      nextStepDescription: {
        value: nextStepDescriptionData,
        error: "",
        required: false,
      },
    });
  }, [descriptionData, nextStepDescriptionData]);

  useEffect(() => {
    if (selectedMeetingReport && isEditMode) {
      setExistingDocuments(selectedMeetingReport.documents);
    }
  }, [selectedMeetingReport, isEditMode]);

  const onTitleChange = (e) => {
    setFormData({
      ...formData,
      title: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onMeetingDateChange = (data) => {
    setFormData({
      ...formData,
      date: {
        value: data,
        error: "",
        required: true,
      },
    });
  };

  const onMeetingNextDate = (data) => {
    setFormData({
      ...formData,
      nextStepDate: {
        value: data,
        error: "",
        required: true,
      },
    });
  };

  const handleRating = (rating) => {
    setFormData({
      ...formData,
      rating: {
        value: rating,
        error: "",
        required: false,
      },
    });
  };
  // document control functions

  const onFileSelect = (e) => {
    let filesArray = formData.documents;
    for (let i = 0; i < e.target.files.length; i++) {
      filesArray.push(e.target.files[i]);
    }
    setFormData({
      ...formData,
      documents: filesArray,
    });
  };

  const deleteFile = (index) => {
    const fileListArr = Array.from(formData.documents);
    fileListArr.splice(index, 1); // here u remove the file

    // recreate file object
    let updatedDocs = [];
    const dT = new DataTransfer();
    if (fileListArr.length > 0) {
      for (let file of fileListArr) {
        dT.items.add(file);
      }
      updatedDocs = dT.files;
    }
    setFormData({
      ...formData,
      documents: updatedDocs,
    });
  };
  const deleteExistingFile = (id) => {
    const documents = existingDocuments.filter((item) => item.id !== id);
    setExistingDocuments(documents);
  };

  const getDocumentList = (files) => {
    let docs = [];
    for (let i = 0; i < files.length; i++) {
      docs[i] = (
        <FileName key={i}>
          <DeleteSpan>
            {" "}
            <Image
              className="deleteImage"
              src={deleteIcon}
              alt="delete"
              onClick={() => deleteFile(i)}
              styles={{ width: "22px", marginTop: "-2px" }}
            />
          </DeleteSpan>{" "}
          {files[i].name}
        </FileName>
      );
    }
    return docs;
  };

  const getExistingDocumentList = (files) => {
    let docs = [];
    for (let i = 0; i < files.length; i++) {
      docs[i] = (
        <FileName key={i}>
          <DeleteSpan>
            {" "}
            <Image
              className="deleteImage"
              src={deleteIcon}
              alt="delete"
              onClick={() => deleteExistingFile(files[i].id)}
            />
          </DeleteSpan>{" "}
          {files[i].fileName}{" "}
        </FileName>
      );
    }
    return docs;
  };

  const validateForm = (data) => {
    let isFormValid = true;
    if (!data.title.value) {
      isFormValid = false;
      data.title.error = t("TITLE_REQUIRED");
    }
    if (!data.date.value) {
      isFormValid = false;
      data.date.error = t("MEETING_DATE_REQUIRED");
    }
    if (!data.rating.value) {
      isFormValid = false;
      data.rating.error = t("MEETING_RATING_REQUIRED");
    }
    if (data.attendees.value <= 0) {
      isFormValid = false;
      data.attendees.error = t("HESUS_PARTICIPANTS_REQUIRED");
    }
    if (data.linkedEntities.value.length <= 0) {
      isFormValid = false;
      data.linkedEntities.error = t("ENTITES_LINKED_REQUIRED");
    }
    if (!isFormValid) {
      setFormData({ ...data });
    }
    return isFormValid;
  };

  const getFormData = () => {
    // Get author ID from localStorage (JWT auth)
    const authorId = UserInfo.getUser().id;

    const dataModified = new FormData();
    dataModified.append("country", countryId);
    dataModified.append("title", formData.title.value);
    dataModified.append("attendees", JSON.stringify(formData.attendees.value));
    dataModified.append("description", formData.description.value);
    dataModified.append("objective", 0);
    dataModified.append("meetingDate", formData.date.value);
    dataModified.append(
      "nextStepDescription",
      formData.nextStepDescription.value
    );
    dataModified.append("nextStepDate", formData.nextStepDate.value);
    dataModified.append("status", JSON.stringify({ id: 1, label: "active" }));
    dataModified.append("rating", formData.rating.value);

    let linkedEntitiesData = { sites: [], companies: [], contacts: [] };
    formData.linkedEntities.value.forEach((entity) => {
      let id = entity.id.split("-");
      if (entity.identity === "SITE") {
        linkedEntitiesData.sites.push({
          id: id[1],
          name: entity.name,
        });
      }
      if (entity.identity === "COMPANY") {
        linkedEntitiesData.companies.push({
          id: id[1],
          name: entity.name,
        });
      }
      if (entity.identity === "CONTACT") {
        linkedEntitiesData.contacts.push({
          id: id[1],
          name: entity.name,
        });
      }
    });
    dataModified.append("linkedEntities", JSON.stringify(linkedEntitiesData));
    dataModified.append("rating", formData.rating.value);

    dataModified.append("createdBy", authorId);
    dataModified.append("createdAt", "");
    dataModified.append("updatedAt", "");
    if (formData.documents.length > 0) {
      for (let i = 0; i < formData.documents.length; i++) {
        dataModified.append(
          `document[${i}][type]`,
          JSON.stringify({
            id: 19,
          })
        );
        dataModified.append(
          `document[${i}][filename]`,
          formData.documents[i].name
        );
        dataModified.append(`document[${i}][meetingReportId]`, id);
        dataModified.append(`document[${i}][file]`, formData.documents[i]);
      }
    }

    if (existingDocuments.length > 0) {
      let i = formData.documents.length > 0 ? formData.documents.length : 0;
      existingDocuments.forEach((document) => {
        dataModified.append(`document[${i}][meetingReportId]`, id);
        dataModified.append(`document[${i}][filename]`, document.fileName);
        dataModified.append(`document[${i}][id]`, document.id);
        dataModified.append(`document[${i}][file]`, document.fileName);
        i += 1;
      });
    }
    return dataModified;
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      const data = getFormData(formData);
      setIsMeetingCreating(true);
      setCreateMeetingAPIError("");
      // Create solution
      if (!id && !isEditMode) {
        axios
          .post(`/meetingReports`, data, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(() => {
            setIsMeetingCreating(false);
            updateRefeshStatus();
            onClose();
          })
          .catch((error) => {
            setIsMeetingCreating(false);
            setCreateMeetingAPIError(error.response?.data?.message ?? "");
          });
      }
      // Edit
      if (id && isEditMode) {
        axios
          .post(`/meetingReports/${id}`, data, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(() => {
            setIsMeetingCreating(false);
            updateRefeshStatus();
            // onClose();
          })
          .catch((error) => {
            setDataSaveStatus(false);
            setIsMeetingCreating(false);
            setCreateMeetingAPIError(error.response?.data?.message ?? "");
          });
      }
    }
  };

  const onEdit = () => {
    togglePopUp();
  };

  return (
    <Dialog>
      <div>
        <HeaderWrapper>
          <Title>{title}</Title>
          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image
              src={closeIcon}
              alt="close meeting report popup"
              styles={{ width: "auto" }}
            />
          </Button>
        </HeaderWrapper>
        {isViewMode && (
          <MeetingReportView meetingReportInfo={selectedMeetingReport} />
        )}
        {!isViewMode && (
          <FormBody>
            <OptionsWrapper>
              <FlexRow>
                <SectionContainer>
                  <div>
                    <GridRow>
                      <TextInput
                        type="text"
                        label={t("TITLE")}
                        // name={selectedMeetingReport.title}
                        id="meeting-report-title"
                        isFloat
                        value={String(formData.title.value)}
                        onChange={onTitleChange}
                        containerStyles={{ marginTop: "21px" }}
                        inputStyles={{ padding: "7px 5px 7px 10px" }}
                        required
                        // disabled={isDisabled}
                        error={formData.title.error}
                        placeholder={t("TITLE")}
                      />
                      <ParentDatePicker>
                        <TextInputLabel> {t("MEETING_DATE")}</TextInputLabel>
                        <DatePicker
                          style={{
                            marginTop: "24px",
                            padding: "5px 10px",
                            backgroundColor: "white",
                            borderColor: "hsl(0deg, 0%, 80%)",
                            borderRadius: "4px",
                            fontFamily: "Lato",
                            fontSize: "16px",
                            height: "auto",
                            width: "100%",
                            lineHeight: "1.4",
                            border: "1px solid #474747",
                          }}
                          title={t("MEETING_DATE")}
                          className={{ ParentDatePicker }}
                          containerClassName={{ ParentDatePicker }}
                          calendarPosition="bottom-center"
                          format={getDatePickerFormat()}
                          locale={getCalendarLocale()}
                          value={
                            getDateFormat(formData.date.value, "YYYY-MM-DD") ||
                            ""
                          }
                          onChange={(e) => {
                            onMeetingDateChange(
                              moment(e.toDate()).format("YYYY-MM-DD")
                            );
                          }}
                          name="meeting-date"
                        />
                      </ParentDatePicker>
                    </GridRow>
                    <MeetingReportContainer>
                      <ContainerLabel htmlFor="container-meeting-summary">
                        {t("MEETING_SUMMARY")}
                      </ContainerLabel>
                      <CKGridRow>
                        <AttendentList
                          formData={formData}
                          setFormData={setFormData}
                          isEditMode={isEditMode}
                          countryId={countryId}
                        />
                      </CKGridRow>
                      <CKGridRow>
                        <EntityList
                          formData={formData}
                          setFormData={setFormData}
                          objectType={objectType}
                          objectId={objectId}
                          isEditMode={isEditMode}
                          countryId={countryId}
                          // onEntityListUpdate={onEntityListUpdate}
                        />
                      </CKGridRow>
                      <CKGridRow>
                        <div>
                          <CKInputLabel htmlFor="description">
                            {t("DESCRIPTION")}
                          </CKInputLabel>
                          <CKEditorText
                            descriptionData={descriptionData}
                            setDescriptionData={setDescriptionData}
                          />
                          {formData.description.error && (
                            <RequiredMessage>
                              {formData.description.error}
                            </RequiredMessage>
                          )}
                        </div>
                      </CKGridRow>
                      <GridRow>
                        <div>
                          <div style={{ marginTop: "20px" }}>
                            <FileInputLabel>{t("DOCUMENTS")}</FileInputLabel>

                            <FileImageWrapper htmlFor="document">
                              <Image
                                src={addIcon}
                                alt="upload meeting report."
                                styles={{ width: "auto" }}
                              />
                            </FileImageWrapper>
                            <FileInput
                              type="file"
                              id="document"
                              multiple
                              onChange={onFileSelect}
                            />
                          </div>
                          {isEditMode && (
                            <DocumentRow>
                              {existingDocuments.length > 0 &&
                                getExistingDocumentList(existingDocuments)}
                              {formData.documents.length > 0
                                ? getDocumentList(formData.documents)
                                : ""}
                            </DocumentRow>
                          )}
                          {!isEditMode && (
                            <DocumentRow>
                              {" "}
                              {getDocumentList(formData.documents)}{" "}
                            </DocumentRow>
                          )}
                        </div>
                        <div className="meeting_report_rating_div">
                          <br></br>
                          <RatingWrapper>{t("RATING")}</RatingWrapper>

                          <br></br>
                          <StarRatingComponent
                            name="rate1"
                            starCount={5}
                            size={24}
                            value={Number(formData?.rating?.value)}
                            onStarClick={handleRating}
                          />
                        </div>
                      </GridRow>
                    </MeetingReportContainer>
                    <MeetingReportContainer>
                      <ContainerLabel htmlFor="container-meeting-summary">
                        {t("NEXT_STEP")}
                      </ContainerLabel>
                      <GridRow>
                        <ParentDatePicker>
                          <TextInputLabel>
                            {" "}
                            {t("MEETING_NEXT_DATE")}
                          </TextInputLabel>
                          <DatePicker
                            style={{
                              marginTop: "24px",
                              padding: "5px 10px",
                              backgroundColor: "white",
                              borderColor: "hsl(0deg, 0%, 80%)",
                              borderRadius: "4px",
                              fontFamily: "Lato",
                              fontSize: "16px",
                              height: "auto",
                              width: "100%",
                              lineHeight: "1.4",
                              border: "1px solid #474747",
                            }}
                            title={t("MEETING_NEXT_DATE")}
                            className={{ ParentDatePicker }}
                            containerClassName={{ ParentDatePicker }}
                            calendarPosition="bottom-center"
                            format={getDatePickerFormat()}
                            locale={getCalendarLocale()}
                            value={
                              getDateFormat(
                                formData.nextStepDate.value,
                                "YYYY-MM-DD"
                              ) || ""
                            }
                            onChange={(e) => {
                              onMeetingNextDate(
                                moment(e.toDate()).format("YYYY-MM-DD")
                              );
                            }}
                            name="next-date"
                          />
                        </ParentDatePicker>
                      </GridRow>
                      <CKGridRow>
                        <div>
                          <CKInputLabel htmlFor="description">
                            {t("DESCRIPTION")}
                          </CKInputLabel>
                          <CKEditorText
                            descriptionData={nextStepDescriptionData}
                            setDescriptionData={setNextDescriptionData}
                          />
                          {formData.nextStepDescription.error && (
                            <RequiredMessage>
                              {formData.nextStepDescription.error}
                            </RequiredMessage>
                          )}
                        </div>
                      </CKGridRow>
                    </MeetingReportContainer>
                  </div>
                </SectionContainer>
              </FlexRow>
            </OptionsWrapper>
          </FormBody>
        )}
        <ButtonWrapper>
          {createMeetingAPIError && <div>{createMeetingAPIError}</div>}
          {isViewMode && selectedMeetingReport?.canEdit === 1 && (
            <Button
              styles={{
                color: "white",
                background: "#1BACE4",
                border: "1px solid #1BACE4",
                marginRight: "0.32rem",
                "&:hover": {
                  backgroundColor: "#1589b6",
                },
              }}
              onClick={onEdit}
            >
              {t("EDIT")}
            </Button>
          )}
          {!isViewMode && (
            <Button
              styles={{
                color: "white",
                background: "#1BACE4",
                border: "1px solid #1BACE4",
                marginRight: "0.32rem",
                "&:hover": {
                  backgroundColor: "#1589b6",
                },
              }}
              onClick={onSubmit}
            >
              <>
                {isMeetingCreating && <ClipLoader size={15} color="#fff" />}
                {!isMeetingCreating && t("SUBMIT")}
              </>
            </Button>
          )}
          <Button
            styles={{
              color: "#333",
              background: "#fff",
              border: "1px solid #ccc",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onClose}
          >
            {t("CLOSE")}
          </Button>
        </ButtonWrapper>
      </div>
    </Dialog>
  );
};

AddEditMeetingReport.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  title: PropTypes.string.isRequired,
  isEditMode: PropTypes.bool,
  isViewMode: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  updateRefeshStatus: PropTypes.func.isRequired,
};

AddEditMeetingReport.defaultProps = {
  id: null,
  isEditMode: false,
  isViewMode: false,
};
export default withTranslation()(AddEditMeetingReport);
